import { useEffect, useRef, useState } from "react";
import { gsap, prefersReducedMotion } from "@/lib/gsapSetup";
import { brand } from "@/data/siteContent";

/**
 * Cinematic page loader. A thin line fills, the wordmark reveals, then the whole
 * overlay masks upward to unveil the hero. Calls onComplete when done.
 */
export default function Loader({ onComplete }) {
  const overlayRef = useRef(null);
  const barRef = useRef(null);
  const wordRef = useRef(null);
  const [count, setCount] = useState(0);

  useEffect(() => {
    const reduced = prefersReducedMotion();
    const tl = gsap.timeline({
      onComplete: () => onComplete && onComplete(),
    });

    const counter = { v: 0 };
    tl.to(counter, {
      v: 100,
      duration: reduced ? 0.4 : 1.6,
      ease: "power2.inOut",
      onUpdate: () => setCount(Math.round(counter.v)),
    });
    tl.fromTo(
      barRef.current,
      { scaleX: 0 },
      { scaleX: 1, duration: reduced ? 0.4 : 1.6, ease: "power2.inOut" },
      0
    );
    tl.to(wordRef.current, { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }, 0.2);

    if (!reduced) {
      tl.to(wordRef.current, { opacity: 0, y: -20, duration: 0.5, ease: "power2.in" }, "+=0.15");
      tl.to(overlayRef.current, {
        yPercent: -100,
        duration: 1,
        ease: "expo.inOut",
      });
    } else {
      tl.to(overlayRef.current, { opacity: 0, duration: 0.4 });
    }

    return () => tl.kill();
  }, [onComplete]);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#050505]"
      data-testid="page-loader"
    >
      <div
        ref={wordRef}
        className="font-display text-4xl md:text-6xl tracking-tight text-white opacity-0 translate-y-4"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        {brand.wordmark}
      </div>
      <div className="mt-8 h-px w-56 md:w-80 overflow-hidden bg-white/10">
        <div ref={barRef} className="h-full w-full origin-left bg-white/80" />
      </div>
      <div className="mt-4 font-mono text-xs tracking-[0.3em] text-white/40">
        {String(count).padStart(3, "0")}
      </div>
    </div>
  );
}
